/**
 * Derived occupancy/entries/multi-select grids for the merged table's
 * availability columns (offer-unified-table, CU-86cbc3r8b) — the "derived
 * grid" half of `use-offer-availability-data.ts`'s data seam; see that
 * file's own doc comment for why this is split out.
 */
import * as React from 'react';
import { buildInventoryAvailabilityGrid, type InventoryAvailabilityPeriod } from '@ooh/screens';
import type { UnitAvailabilityRow } from '@ooh/contracts';
import {
  buildOfferUnitEntriesGrid,
  mapOfferUnitsToOccupancy,
  unitIdsWithOwnReservation,
} from '../lib/offer-availability-mapping';
import type { OfferUnitItem } from '../units-tab-columns';

export interface UseOfferAvailabilityGridsOptions {
  rows: OfferUnitItem[];
  rowIds: number[];
  periods: InventoryAvailabilityPeriod[];
  units: UnitAvailabilityRow[];
  /**
   * Units from the toggle/window-independent reservation-check fetch (see
   * `use-offer-availability-query.ts`) — used ONLY to derive `reservedUnitIds`
   * below, never for the rendered occupancy/entries grids (which stay scoped
   * to `units`, the caller's currently rendered window).
   */
  reservationCheckUnits: UnitAvailabilityRow[];
}

export function useOfferAvailabilityGrids({
  rows,
  rowIds,
  periods,
  units,
  reservationCheckUnits,
}: UseOfferAvailabilityGridsOptions) {
  const occupancyByRowId = React.useMemo(() => mapOfferUnitsToOccupancy(units), [units]);

  const entriesGrid = React.useMemo(
    () => buildOfferUnitEntriesGrid(units, periods),
    [units, periods],
  );
  // The TTL countdown renders from `InspectPopover`'s own internal
  // `dominant.ttlExpiresAt` derivation (entries already carry it) — no
  // separate cell-face TTL badge exists on this column-based rendering
  // model, matching the catalog's own `buildRichAvailabilityColumns` usage
  // exactly (no `getOwnCellTtlExpiresAt`-equivalent prop on `RichAvailabilityCell`).

  const reservedUnitIds = React.useMemo(() => {
    const activeIds = unitIdsWithOwnReservation(reservationCheckUnits);
    const ids = new Set<OfferUnitItem['id']>();
    for (const row of rows) {
      if (activeIds.has(String(row.id))) ids.add(row.id);
    }
    return ids;
  }, [rows, reservationCheckUnits]);

  const gridRows = React.useMemo(
    () => rowIds.map((id) => ({ id: String(id), label: '' })),
    [rowIds],
  );
  const grid = React.useMemo(
    () => buildInventoryAvailabilityGrid(rowIds.map(String), periods, occupancyByRowId),
    [rowIds, periods, occupancyByRowId],
  );

  return { occupancyByRowId, entriesGrid, reservedUnitIds, gridRows, grid };
}
